// Iubenda helpers — reload the cookie banner in a new language on SPA switch.
// The v3 Cookie Solution script has no public API to swap language at runtime,
// so we tear down all its DOM + globals and re-inject the script tag with a
// fresh csConfiguration. This is the same effect as a page reload but keeps
// the SPA route + component state intact.

const IUBENDA_SCRIPT_SRC = '//cdn.iubenda.com/cs/iubenda_cs.js';
const IUBENDA_SITE_ID = 2561084;
const IUBENDA_COOKIE_POLICY_ID = 70068662;
const IUBENDA_CONSENT_COOKIE = `_iub_cs-${IUBENDA_COOKIE_POLICY_ID}`;
const SUPPORTED_LANGS = ['it', 'en', 'fr', 'de', 'es'];

const hasStoredConsent = () => {
  if (typeof document === 'undefined') return false;
  // Two independent signals: (1) Iubenda's own cookie, (2) our localStorage flag
  // written from the accept/reject callbacks. Either one means the user has
  // already answered the banner and we must NOT show it again.
  if (document.cookie.includes(`${IUBENDA_CONSENT_COOKIE}=`)) return true;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('lg_iub_consent_v1')) return true;
  } catch { /* localStorage may throw in private mode — treat as no-consent */ }
  return false;
};

const markConsented = () => {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem('lg_iub_consent_v1', String(Date.now())); } catch { /* noop */ }
  if (document && document.documentElement) {
    document.documentElement.classList.add('lg-hide-iub');
  }
};

const buildConfig = (lang) => ({
  consentOnContinuedBrowsing: false,
  floatingPreferencesButtonDisplay: 'bottom-left',
  // Keep local consent even if the Iubenda log-server call fails (ad-blockers,
  // preview host not in whitelist, flaky network). Setting this to `true` makes
  // the banner reappear on every refresh in those cases — see index.html for
  // the matching config used at first paint.
  invalidateConsentWithoutLog: false,
  perPurposeConsent: true,
  siteId: IUBENDA_SITE_ID,
  whitelabel: false,
  cookiePolicyId: IUBENDA_COOKIE_POLICY_ID,
  lang,
  banner: {
    acceptButtonDisplay: true,
    closeButtonRejects: true,
    customizeButtonDisplay: true,
    explicitWithdrawal: true,
    listPurposes: true,
    position: 'bottom',
    rejectButtonDisplay: true,
  },
  // Same defensive callbacks as index.html — persist a local flag + hide via CSS
  // so subsequent refreshes/language switches skip the banner even when Iubenda
  // fails to validate consent server-side (preview host not in whitelist).
  callback: {
    onConsentGiven: markConsented,
    onConsentFirstGiven: markConsented,
    onConsentRejected: markConsented,
    onPreferenceFirstExpressed: markConsented,
  },
});



/**
 * Reload the Iubenda Cookie Solution in a new language.
 * Safe to call before the script has finished loading; does nothing if the
 * current language already matches. If the user has already given/rejected
 * consent, we ONLY update the config-lang property in-place without touching
 * the DOM — this prevents the banner from briefly re-appearing during SPA
 * language switches on hostnames that aren't in the Iubenda whitelist.
 */
export function reloadIubendaWithLang(lang) {
  if (typeof window === 'undefined') return;
  if (!SUPPORTED_LANGS.includes(lang)) return;
  const currentLang = window._iub && window._iub.csConfiguration && window._iub.csConfiguration.lang;
  if (currentLang === lang) return;

  // Fast path — user already consented. Just update the lang in the config
  // (Iubenda reads it when opening the preferences panel via `openPreferences`)
  // and skip the destructive re-inject. The banner stays hidden.
  if (hasStoredConsent()) {
    if (window._iub && window._iub.csConfiguration) {
      window._iub.csConfiguration.lang = lang;
    }
    return;
  }

  // First-visit path — no consent yet. Tear down + re-inject so the banner
  // renders in the freshly picked language.

  // 0. Defensively remove any stale hide class (should never be set at this point,
  //    but be robust to bugs)
  if (document && document.documentElement) {
    document.documentElement.classList.remove('lg-hide-iub');
  }

  // 1. Remove any existing Iubenda DOM (banner, floating button, preferences panel, etc.)
  document
    .querySelectorAll(
      '#iubenda-cs-banner, #iubenda-cs-container, [id^="iubenda-cs"], [id^="iubenda-iframe"], .iubenda-tp-btn, [class^="iubenda-cs"]',
    )
    .forEach((el) => el.remove());

  // 2. Remove the previous script tag so re-injection triggers a fresh load
  document
    .querySelectorAll('script[src*="iubenda.com/cs/"]')
    .forEach((el) => el.remove());

  // 3. Reset the global _iub with a fresh config in the new language
  window._iub = [];
  window._iub.csConfiguration = buildConfig(lang);

  // 4. Re-inject the script tag — Iubenda will read the new config on init
  const script = document.createElement('script');
  script.src = IUBENDA_SCRIPT_SRC;
  script.async = true;
  script.setAttribute('charset', 'UTF-8');
  document.head.appendChild(script);
}
